import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, 
  User, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronRight,
  FileText,
  Download,
  Search,
  Filter,
  Target,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PlaylistAudioPlayer } from '../components/calls/PlaylistAudioPlayer';
import { CallAnalysisReportModal } from '../components/calls/CallAnalysisReportModal';
import { apiClient } from '../lib/api/client';

interface CallInfo {
  call_sid: string;
  date: string;
  duration_seconds: number;
  duration_formatted: string;
  audio_url: string | null;
}

interface ReportInfo {
  report_id: string | null;
  overall_score: number | null;
  fitment_score: number | null;
  recommendation: string | null;
  created_at: string | null;
}

interface SessionInfo {
  session_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_phone: string | null;
  requirement_id: string | null;
  requirement_title: string | null;
  client_name: string | null;
  calls: CallInfo[];
  total_calls: number;
  total_duration_seconds: number;
  total_duration_formatted: string;
  date_range: {
    first: string | null;
    last: string | null;
  };
  report: ReportInfo | null;
  has_transcription: boolean;
}

interface RecruiterSessions {
  recruiter_id: string;
  recruiter_name: string;
  recruiter_email: string | null;
  total_sessions: number;
  sessions: SessionInfo[];
}

interface CallRecordingsData {
  recruiters: RecruiterSessions[];
  total_recruiters: number;
  total_sessions: number;
}

const getRecommendationBadge = (recommendation: string | null) => {
  if (!recommendation) return null;
  
  const rec = recommendation.toUpperCase();
  if (rec.includes('STRONG') || rec.includes('YES') || rec === 'RECOMMENDED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle className="w-3 h-3" />
        Recommended
      </span>
    );
  }
  if (rec.includes('CONSIDER') || rec.includes('MAYBE')) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
        <AlertCircle className="w-3 h-3" />
        Consider
      </span>
    );
  }
  if (rec.includes('NO') || rec.includes('NOT')) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <XCircle className="w-3 h-3" />
        Not Recommended
      </span>
    );
  }
  return null;
};

export const TeamLeadCallRecordingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CallRecordingsData | null>(null);
  
  // Filters
  const [selectedRecruiter, setSelectedRecruiter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  
  // UI State
  const [expandedRecruiters, setExpandedRecruiters] = useState<Set<string>>(new Set());
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  
  // Report Modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<{ name: string; id: string } | null>(null);
  const [selectedRequirement, setSelectedRequirement] = useState<{ title: string; id: string } | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Recruiters list for filter
  const [recruiters, setRecruiters] = useState<{ user_id: string; name: string }[]>([]);

  useEffect(() => {
    fetchRecruiters();
    fetchCallRecordings();
  }, []);

  const fetchRecruiters = async () => {
    console.log('📞 [CallRecordings] Fetching recruiters...');
    console.log('📞 [CallRecordings] API Base URL:', apiClient.client.defaults.baseURL);
    try {
      const response = await apiClient.client.get('/api/v1/team-lead/recruiters');
      console.log('📞 [CallRecordings] Recruiters response:', response.data);
      setRecruiters(response.data.recruiters || []);
    } catch (err: any) {
      console.error('❌ [CallRecordings] Failed to fetch recruiters:', err);
      console.error('❌ [CallRecordings] Error details:', err.response?.data || err.message);
    }
  };

  const fetchCallRecordings = async () => {
    console.log('📞 [CallRecordings] Fetching call recordings...');
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (selectedRecruiter !== 'all') {
        params.append('recruiter_id', selectedRecruiter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (dateRange.start) {
        params.append('start_date', dateRange.start);
      }
      if (dateRange.end) {
        params.append('end_date', dateRange.end);
      }
      
      const url = `/api/v1/team-lead/call-recordings?${params}`;
      console.log('📞 [CallRecordings] Calling:', url);
      const response = await apiClient.client.get(url);
      console.log('📞 [CallRecordings] Response:', response.data);
      setData(response.data);
      
      // Auto-expand first recruiter if only one
      if (response.data.recruiters?.length === 1) {
        setExpandedRecruiters(new Set([response.data.recruiters[0].recruiter_id]));
      }
    } catch (err: any) {
      console.error('❌ [CallRecordings] Failed to fetch call recordings:', err);
      console.error('❌ [CallRecordings] Error details:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Failed to load call recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCallRecordings();
  };

  const toggleRecruiter = (recruiterId: string) => {
    setExpandedRecruiters(prev => {
      const next = new Set(prev);
      if (next.has(recruiterId)) {
        next.delete(recruiterId);
      } else {
        next.add(recruiterId);
      }
      return next;
    });
  };

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const handleViewReport = async (session: SessionInfo) => {
    if (!session.report?.report_id) return;
    
    try {
      setLoadingReport(true);
      setSelectedCandidate({ name: session.candidate_name, id: session.candidate_id });
      setSelectedRequirement({ 
        title: session.requirement_title || 'Unknown Position', 
        id: session.requirement_id || '' 
      });
      
      // Fetch the full report
      const response = await apiClient.client.get(
        `/api/v1/call-analysis/report/${session.candidate_id}/${session.requirement_id || 'default'}`
      );
      
      setSelectedReport(response.data);
      setShowReportModal(true);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      // Still show modal with basic info
      setSelectedReport({
        overall_score: session.report?.overall_score,
        fitment_score: session.report?.fitment_score,
        hire_recommendation: session.report?.recommendation
      });
      setShowReportModal(true);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadAll = (session: SessionInfo) => {
    // Download all recordings for this session
    session.calls.forEach((call, index) => {
      if (call.audio_url) {
        setTimeout(() => {
          const link = document.createElement('a');
          link.href = call.audio_url!;
          link.download = `${session.candidate_name}_call_${index + 1}.mp3`;
          link.click();
        }, index * 500); // Stagger downloads
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading call recordings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Recordings</h3>
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={fetchCallRecordings} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Phone className="w-7 h-7 text-indigo-500" />
            Team Call Recordings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {data?.total_sessions || 0} candidate sessions from {data?.total_recruiters || 0} recruiters
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Recruiter Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Filter className="w-4 h-4 inline mr-1" />
              Recruiter
            </label>
            <select
              value={selectedRecruiter}
              onChange={(e) => setSelectedRecruiter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Recruiters ({recruiters.length})</option>
              {recruiters.map(rec => (
                <option key={rec.user_id} value={rec.user_id}>
                  {rec.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Search className="w-4 h-4 inline mr-1" />
              Search Candidate
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name..."
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Date Range */}
          <div className="flex gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                From
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <Button onClick={handleSearch} className="px-6">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </div>
      </div>

      {/* Results */}
      {!data?.recruiters?.length ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Phone className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Call Recordings Found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            No recordings match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.recruiters.map(recruiter => (
            <div
              key={recruiter.recruiter_id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Recruiter Header */}
              <button
                onClick={() => toggleRecruiter(recruiter.recruiter_id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {recruiter.recruiter_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {recruiter.recruiter_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {recruiter.total_sessions} candidate sessions
                    </p>
                  </div>
                </div>
                {expandedRecruiters.has(recruiter.recruiter_id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Sessions */}
              {expandedRecruiters.has(recruiter.recruiter_id) && (
                <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {recruiter.sessions.map(session => (
                    <div key={session.session_id} className="p-4">
                      {/* Session Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {session.candidate_name}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {session.requirement_title}
                              {session.client_name && ` @ ${session.client_name}`}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {session.total_calls} calls
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {session.total_duration_formatted}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {session.date_range.first} - {session.date_range.last}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Score Badge */}
                          {session.report?.fitment_score && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                              <Target className="w-4 h-4 text-indigo-500" />
                              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                                {session.report.fitment_score}%
                              </span>
                            </div>
                          )}
                          
                          {/* Recommendation Badge */}
                          {getRecommendationBadge(session.report?.recommendation || null)}
                          
                          {/* Expand/Collapse */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleSession(session.session_id)}
                          >
                            {expandedSessions.has(session.session_id) ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedSessions.has(session.session_id) && (
                        <div className="mt-4 space-y-4">
                          {/* Audio Player */}
                          <PlaylistAudioPlayer calls={session.calls} />
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-3">
                            {session.report?.report_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReport(session)}
                                disabled={loadingReport}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                View Report
                              </Button>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadAll(session)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download All
                            </Button>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/candidates/${session.candidate_id}`)}
                            >
                              View Candidate Profile →
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && selectedReport && (
        <CallAnalysisReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setSelectedReport(null);
          }}
          report={selectedReport}
          candidateId={selectedCandidate?.id || ''}
          requirementId={selectedRequirement?.id || ''}
          candidateName={selectedCandidate?.name}
          requirementTitle={selectedRequirement?.title}
        />
      )}
    </div>
  );
};

export default TeamLeadCallRecordingsPage;

